/**
 * Equipment Check Page
 *
 * Post-rental inspection workflow. See docs/EQUIPMENT_CHECK_SPEC.md.
 *
 * Phase 2: wired to real data (queue, mark-checked + photo upload,
 * report-issue, block, swap RPC, upcoming handovers).
 * Mobile-first card layout; reuses BottomSheet for sheets and drawer.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Calendar,
  Camera,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Image as ImageIcon,
  Info,
  Loader2,
  Lock,
  MapPin,
  Package,
  Search,
  Trash2,
  User,
  Wrench,
  X,
} from 'lucide-react';
import BottomSheet from '../components/BottomSheet';
import ReportIssueModal from '../components/ReportIssueModal';
import ReturnReportDetail from '../components/ReturnReportDetail';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getLocations } from '../services/locations.service';
import { getMerchantById } from '../services/merchants.service';
import {
  computePriority,
  ensureEquipmentChecksForOverdue,
  getBikeDetail,
  getEquipmentChecks,
  getEquipmentCheckPhotoSignedUrls,
  getInventoryItemsAtLocation,
  getSwapCandidates,
  getUpcomingHandovers,
  markEquipmentChecked,
  markObsoleteForStartedRentals,
  setEquipmentCheckIssue,
  swapBookingItemEquipment,
  type BikeDetail,
  type BikeListItem,
  type CustomerReturnBadge,
  type EquipmentCheckListItem,
  type PriorityTier,
  type SwapCandidate,
  type UpcomingHandover,
} from '../services/equipment-checks.service';
import { createBlockingTime } from '../services/inventoryBlockingTimes.service';
import { resolveIssue } from '../services/issues.service';

// ---------- Helpers ---------------------------------------------------------

type TFn = (key: string, opts?: Record<string, unknown>) => string;

function timeAgo(iso: string | null, t: TFn): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const minutes = Math.round(abs / 60_000);
  const hours = Math.round(abs / 3_600_000);
  const days = Math.round(abs / 86_400_000);
  const key = diff >= 0 ? 'equipmentCheck.ago' : 'equipmentCheck.in';
  if (minutes < 60) return t(key, { n: minutes, unit: t('equipmentCheck.unitMin') });
  if (hours < 24)   return t(key, { n: hours,   unit: t('equipmentCheck.unitHour') });
  return t(key, { n: days, unit: t('equipmentCheck.unitDay') });
}

function formatNextOut(iso: string, t: TFn, language: string): string {
  const date = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const hhmm = date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  if (sameDay(date, today))    return `${t('equipmentCheck.today')} ${hhmm}`;
  if (sameDay(date, tomorrow)) return `${t('equipmentCheck.tomorrow')} ${hhmm}`;
  return date.toLocaleDateString(language, { weekday: 'short', day: 'numeric', month: 'short' }) + ` ${hhmm}`;
}

function customerBadgeLabel(badge: CustomerReturnBadge, t: TFn): { label: string; tone: 'ok' | 'warn' | 'danger' | 'neutral' } {
  switch (badge.kind) {
    case 'item_ok':        return { label: t('equipmentCheck.customerItemOk'),       tone: 'ok' };
    case 'item_issue':     return { label: t('equipmentCheck.customerItemIssue'),    tone: 'warn' };
    case 'item_damaged':   return { label: t('equipmentCheck.customerItemDamaged'),  tone: 'danger' };
    case 'item_missing':   return { label: t('equipmentCheck.customerItemMissing'),  tone: 'danger' };
    case 'booking_ok':     return { label: t('equipmentCheck.customerBookingOk'),    tone: 'ok' };
    case 'booking_issues': return { label: t('equipmentCheck.customerBookingIssues'), tone: 'warn' };
    case 'none':           return { label: t('equipmentCheck.noCustomerReport'),     tone: 'neutral' };
  }
}

const priorityColor: Record<PriorityTier, string> = {
  P1: 'bg-danger',
  P2: 'bg-danger',
  P3: 'bg-warning',
  P4: 'bg-warning',
  P5: 'bg-success',
  P6: 'bg-secondary',
};

interface LocationOption {
  id: string;
  name: string;
  two_emails_enabled: boolean | null;
  time_cut_off: number | null;
  days_cut_off: number | null;
  cut_off_codes: string | null;
}

// Mirrors the swap-availability check in the DB / NewBookingFlow.tsx.
function isPastCutoff(loc: LocationOption | undefined, startIso: string | null): boolean {
  if (!loc || !startIso) return true;
  const start = new Date(startIso);
  const now = new Date();
  if (loc.time_cut_off != null && loc.time_cut_off > 0) {
    const cutoff = new Date(start.getTime() - loc.time_cut_off * 3_600_000);
    return now >= cutoff;
  }
  if (loc.days_cut_off != null && loc.days_cut_off >= 0 && loc.cut_off_codes) {
    const cutoff = new Date(start);
    cutoff.setDate(cutoff.getDate() - loc.days_cut_off);
    const [h, m] = loc.cut_off_codes.split(':').map(Number);
    cutoff.setHours(h, m || 0, 0, 0);
    return now >= cutoff;
  }
  return true;
}

// Build a minimal EquipmentCheckListItem so that the existing Block /
// Report-issue sheets can be reused from the Bikes tab (no booking context).
function makeSyntheticCheck(bike: BikeListItem, locationId: string): EquipmentCheckListItem {
  return {
    id: '',
    merchant_id: '',
    location_id: locationId,
    booking_id: '',
    inventory_item_id: bike.inventory_item_id,
    status: 'pending',
    checked_by: null,
    checked_by_name: null,
    checked_at: null,
    note: null,
    photo_paths: [],
    issue_id: null,
    obsolete_at: null,
    obsolete_reason: null,
    created_at: '',
    updated_at: '',
    order_id: '',
    customer_name: '',
    booking_start_date: '',
    booking_end_date: '',
    booking_returned_at: null,
    booking_state: '',
    item_number: bike.item_number,
    combination_code: bike.combination_code,
    product_type_id: bike.product_type_id,
    product_name: bike.product_name,
    customer_return: { kind: 'none' },
    customer_return_photos: 0,
    next_booking_id: null,
    next_booking_start: null,
    next_booking_end: null,
    next_booking_customer: null,
    open_issues: [],
    issue_title: null,
    issue_severity: null,
    customer_flagged_after_check: false,
  };
}

function swapAllowedFor(check: EquipmentCheckListItem, loc: LocationOption | undefined): boolean {
  if (!loc) return false;
  if (!check.next_booking_start) return false; // no upcoming booking on this item
  // For swap rules we only need: upcoming + two_emails_enabled + before cutoff.
  // The DB function enforces the rest (same product_type, availability, conflicts).
  if (new Date(check.next_booking_start) <= new Date()) return false;
  if (!loc.two_emails_enabled) return false;
  return !isPastCutoff(loc, check.next_booking_start);
}

// ---------- Page ------------------------------------------------------------

type Tab = 'queue' | 'handovers' | 'bikes';
type StatusFilter = 'pending' | 'checked' | 'all';

type QueueView =
  | { kind: 'priority'; urgent: EquipmentCheckListItem[]; attention: EquipmentCheckListItem[]; later: EquipmentCheckListItem[] }
  | { kind: 'flat'; items: EquipmentCheckListItem[] }
  | { kind: 'split'; urgent: EquipmentCheckListItem[]; attention: EquipmentCheckListItem[]; later: EquipmentCheckListItem[]; completed: EquipmentCheckListItem[] };

export default function EquipmentCheckPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, merchantId, locationIds, accessControl } = useAuth();
  // When the user is pinned to a single location, default the switcher to it;
  // otherwise (all / multiple locations) start with no pre-selected location.
  const defaultActiveLocationId =
    locationIds && locationIds.length === 1 ? locationIds[0] : null;
  const { currentLanguage, defaultLanguage } = useLanguage();
  const { showToast } = useToast();

  // Feature flag + role gate
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!merchantId) return;
      const { data } = await getMerchantById(merchantId);
      if (!cancelled) setEnabled(((data as any)?.equipment_check_enabled) === true);
    }
    run();
    return () => { cancelled = true; };
  }, [merchantId]);

  // Locations (for switcher + cut-off computation)
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(defaultActiveLocationId);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!merchantId) return;
      const { data } = await getLocations(accessControl, { active: true });
      if (cancelled || !data) return;
      const opts: LocationOption[] = (data as any[]).map((l) => ({
        id: l.id,
        name: l.location_name,
        two_emails_enabled: l.two_emails_enabled,
        time_cut_off: l.time_cut_off,
        days_cut_off: l.days_cut_off,
        cut_off_codes: l.cut_off_codes,
      }));
      setLocations(opts);
      if (!activeLocationId && opts.length > 0) setActiveLocationId(opts[0].id);
    }
    run();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const activeLocation = locations.find((l) => l.id === activeLocationId);

  const [tab, setTab] = useState<Tab>('queue');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [todayOnly, setTodayOnly] = useState(false);
  const [queueSearch, setQueueSearch] = useState('');
  const [bikeSearch, setBikeSearch] = useState('');
  // "Show more" pagination for the completed half of the queue (Checked tab
  // and the "Recently completed" section of All). Resets when the filter
  // changes so each filter has its own pagination history.
  const COMPLETED_PAGE_SIZE = 50;
  const [completedLimit, setCompletedLimit] = useState(COMPLETED_PAGE_SIZE);
  const [hasMoreCompleted, setHasMoreCompleted] = useState(false);
  useEffect(() => { setCompletedLimit(COMPLETED_PAGE_SIZE); }, [statusFilter]);

  // Sheet / drawer state
  const [drawerCheck, setDrawerCheck] = useState<EquipmentCheckListItem | null>(null);
  const [checkedSheet, setCheckedSheet] = useState<EquipmentCheckListItem | null>(null);
  const [issueSheet, setIssueSheet] = useState<EquipmentCheckListItem | null>(null);
  const [blockSheet, setBlockSheet] = useState<EquipmentCheckListItem | null>(null);
  const [swapSheet, setSwapSheet] = useState<EquipmentCheckListItem | null>(null);
  const [returnReportFor, setReturnReportFor] = useState<EquipmentCheckListItem | null>(null);

  // Data
  const [checks, setChecks] = useState<EquipmentCheckListItem[]>([]);
  const [loadingChecks, setLoadingChecks] = useState(false);
  const [handovers, setHandovers] = useState<UpcomingHandover[]>([]);
  const [loadingHandovers, setLoadingHandovers] = useState(false);
  const [bikes, setBikes] = useState<BikeListItem[]>([]);
  const [loadingBikes, setLoadingBikes] = useState(false);
  const [bikeDetailFor, setBikeDetailFor] = useState<BikeListItem | null>(null);
  const [photoViewerUrl, setPhotoViewerUrl] = useState<string | null>(null);

  const reloadQueue = useCallback(async () => {
    if (!merchantId || !activeLocationId) return;
    setLoadingChecks(true);
    // Housekeeping before listing, awaited so the query reflects the latest state:
    //   1. Create auto-rows for overdue bookings without a return report
    //   2. Mark pending rows obsolete if the bike has gone out on a newer rental
    await Promise.all([
      ensureEquipmentChecksForOverdue({ merchantId, locationId: activeLocationId }).catch(() => null),
      markObsoleteForStartedRentals({ merchantId, locationId: activeLocationId }).catch(() => null),
    ]);
    const { data, has_more_completed, error } = await getEquipmentChecks({
      merchantId,
      locationId: activeLocationId,
      status: statusFilter,
      language: currentLanguage,
      defaultLanguage,
      completedLimit,
    });
    if (error) showToast(error, 'error');
    setChecks(data);
    setHasMoreCompleted(has_more_completed);
    setLoadingChecks(false);
  }, [merchantId, activeLocationId, statusFilter, currentLanguage, defaultLanguage, completedLimit, showToast]);

  const reloadHandovers = useCallback(async () => {
    if (!merchantId || !activeLocationId) return;
    setLoadingHandovers(true);
    const { data, error } = await getUpcomingHandovers({
      merchantId,
      locationId: activeLocationId,
      language: currentLanguage,
      defaultLanguage,
    });
    if (error) showToast(error, 'error');
    setHandovers(data);
    setLoadingHandovers(false);
  }, [merchantId, activeLocationId, currentLanguage, defaultLanguage, showToast]);

  const reloadBikes = useCallback(async () => {
    if (!merchantId || !activeLocationId) return;
    setLoadingBikes(true);
    const { data, error } = await getInventoryItemsAtLocation({
      merchantId,
      locationId: activeLocationId,
      language: currentLanguage,
      defaultLanguage,
    });
    if (error) showToast(error, 'error');
    setBikes(data);
    setLoadingBikes(false);
  }, [merchantId, activeLocationId, currentLanguage, defaultLanguage, showToast]);

  useEffect(() => {
    if (tab === 'queue') reloadQueue();
  }, [tab, reloadQueue]);

  useEffect(() => {
    if (tab === 'handovers') reloadHandovers();
  }, [tab, reloadHandovers]);

  useEffect(() => {
    if (tab === 'bikes') reloadBikes();
  }, [tab, reloadBikes]);

  // Compute view shape per filter:
  //   - 'pending':  priority groups (existing).
  //   - 'checked':  flat list, newest-first by checked_at.
  //   - 'all':      pending in priority groups + "Recently completed"
  //                 (checked + obsolete, newest-first).
  const view: QueueView = useMemo(() => {
    const now = new Date();
    const q = queueSearch.trim().toLowerCase();
    const matchesSearch = (c: EquipmentCheckListItem) => {
      if (!q) return true;
      return (
        c.item_number.toLowerCase().includes(q) ||
        c.customer_name.toLowerCase().includes(q) ||
        c.order_id.toLowerCase().includes(q) ||
        (c.product_name || '').toLowerCase().includes(q)
      );
    };

    const byPriority = (a: EquipmentCheckListItem, b: EquipmentCheckListItem) => {
      const aNext = a.next_booking_start ? new Date(a.next_booking_start).getTime() : Infinity;
      const bNext = b.next_booking_start ? new Date(b.next_booking_start).getTime() : Infinity;
      return aNext - bNext;
    };
    const byCompletionDesc = (a: EquipmentCheckListItem, b: EquipmentCheckListItem) => {
      const aT = a.checked_at ?? a.obsolete_at ?? a.updated_at;
      const bT = b.checked_at ?? b.obsolete_at ?? b.updated_at;
      return new Date(bT).getTime() - new Date(aT).getTime();
    };

    const buildPriorityGroups = (rows: EquipmentCheckListItem[]) => {
      const urgent: EquipmentCheckListItem[] = [];
      const attention: EquipmentCheckListItem[] = [];
      const later: EquipmentCheckListItem[] = [];
      const enriched = rows.map((c) => ({ c, p: computePriority(c, now) }));
      if (todayOnly) {
        for (const x of enriched.filter((y) => y.p === 'P1')) urgent.push(x.c);
      } else {
        for (const x of enriched) {
          if (x.p === 'P1' || x.p === 'P2') urgent.push(x.c);
          else if (x.p === 'P3' || x.p === 'P4') attention.push(x.c);
          else later.push(x.c);
        }
      }
      urgent.sort(byPriority);
      attention.sort(byPriority);
      later.sort(byPriority);
      return { urgent, attention, later };
    };

    const filtered = checks.filter(matchesSearch);

    if (statusFilter === 'pending') {
      const g = buildPriorityGroups(filtered);
      return { kind: 'priority', ...g };
    }

    if (statusFilter === 'checked') {
      const items = [...filtered].sort(byCompletionDesc);
      return { kind: 'flat', items };
    }

    // 'all' — split into priority-grouped pending + sorted completed list
    const pendingRows = filtered.filter((c) => c.status === 'pending');
    const completedRows = filtered
      .filter((c) => c.status === 'checked' || c.status === 'obsolete')
      .sort(byCompletionDesc);
    const g = buildPriorityGroups(pendingRows);
    return { kind: 'split', ...g, completed: completedRows };
  }, [checks, statusFilter, todayOnly, queueSearch]);

  // ---- Action handlers ----

  const handleMarkChecked = async (id: string, note: string, photos: File[]) => {
    if (!merchantId || !activeLocationId || !user?.id) return;
    const { ok, error } = await markEquipmentChecked({
      id, merchantId, locationId: activeLocationId, userId: user.id,
      note: note.trim() || null, photos,
    });
    if (!ok) {
      showToast(error || t('equipmentCheck.toastMarkedChecked'), 'error');
      return;
    }
    showToast(t('equipmentCheck.toastMarkedChecked'), 'success');
    setCheckedSheet(null);
    reloadQueue();
  };

  const handleIssueCreated = async (newIssue: { id: string }, checkId: string | null) => {
    if (checkId) {
      await setEquipmentCheckIssue({ id: checkId, issueId: newIssue.id });
    }
    showToast(t('equipmentCheck.toastIssueRaised'), 'success');
    setIssueSheet(null);
    if (tab === 'queue') reloadQueue();
    if (tab === 'bikes') reloadBikes();
  };

  const handleBlock = async (
    check: EquipmentCheckListItem, blockUntil: string | null, reason: string
  ) => {
    if (!reason.trim()) {
      showToast('Please enter a reason', 'error');
      return;
    }
    // The blocking_reason column is a category (enum-ish: 'maintenance' /
    // 'repair' / etc.) — the user's free text goes into notes.
    const indefiniteIso = '2099-12-31T23:59:59Z';
    const { error } = await createBlockingTime({
      inventory_item_id: check.inventory_item_id,
      blocked_from: new Date().toISOString(),
      blocked_until: blockUntil ? new Date(blockUntil + 'T23:59:59').toISOString() : indefiniteIso,
      blocking_reason: 'maintenance',
      notes: reason.trim(),
    } as any);
    if (error) {
      showToast(error, 'error');
      return;
    }
    showToast(t('equipmentCheck.toastItemBlocked'), 'success');
    setBlockSheet(null);
    if (tab === 'bikes') reloadBikes();
  };

  const handleSwap = async (check: EquipmentCheckListItem, newInventoryItemId: string) => {
    const { ok, error } = await swapBookingItemEquipment({
      bookingId: check.booking_id,
      oldInventoryItemId: check.inventory_item_id,
      newInventoryItemId,
    });
    if (!ok) {
      showToast(error || 'Swap failed', 'error');
      return;
    }
    showToast(t('equipmentCheck.toastEquipmentSwapped'), 'success');
    setDrawerCheck(null);
    reloadQueue();
  };

  const handleNavigateToIssue = (issueId: string) => {
    setDrawerCheck(null);
    setBikeDetailFor(null);
    navigate(`/issues/${issueId}`);
  };

  const handleResolveIssue = async (issueId: string): Promise<boolean> => {
    if (!user?.id) return false;
    const { error } = await resolveIssue(issueId, user.id, { resolution_type: 'repaired' });
    if (error) {
      showToast(error, 'error');
      return false;
    }
    showToast(t('equipmentCheck.toastIssueResolved'), 'success');
    if (tab === 'queue') reloadQueue();
    if (tab === 'bikes') reloadBikes();
    return true;
  };

  // ---- Render gates ----

  if (enabled === null) {
    return (
      <div className="p-md sm:p-lg flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
      </div>
    );
  }

  if (enabled === false) {
    return (
      <div className="p-md sm:p-lg max-w-5xl mx-auto">
        <div className="card p-lg text-center">
          <Info className="w-10 h-10 text-secondary mx-auto mb-md" />
          <h2 className="text-lg font-semibold text-primary mb-sm">{t('equipmentCheck.notEnabledTitle')}</h2>
          <p className="text-sm text-secondary">{t('equipmentCheck.notEnabledHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-md sm:p-lg max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-md mb-md">
        <ClipboardCheck className="w-7 h-7 text-blue" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-primary">{t('navigation.equipmentCheck')}</h1>
          <p className="text-sm text-secondary">{t('equipmentCheck.subtitle')}</p>
        </div>
        {locations.length > 1 && (
          <div className="hidden sm:flex items-center gap-sm">
            <MapPin className="w-4 h-4 text-secondary" />
            <select
              value={activeLocationId || ''}
              onChange={(e) => setActiveLocationId(e.target.value)}
              className="bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm"
            >
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-primary mb-md overflow-x-auto">
        <button
          onClick={() => setTab('queue')}
          className={`px-md py-sm text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            tab === 'queue' ? 'border-blue text-blue' : 'border-transparent text-secondary hover:text-primary'
          }`}
        >
          {t('equipmentCheck.tabQueue')}
        </button>
        <button
          onClick={() => setTab('handovers')}
          className={`px-md py-sm text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            tab === 'handovers' ? 'border-blue text-blue' : 'border-transparent text-secondary hover:text-primary'
          }`}
        >
          {t('equipmentCheck.tabHandovers')}
        </button>
        <button
          onClick={() => setTab('bikes')}
          className={`px-md py-sm text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
            tab === 'bikes' ? 'border-blue text-blue' : 'border-transparent text-secondary hover:text-primary'
          }`}
        >
          {t('equipmentCheck.tabBikes')}
        </button>
      </div>

      {tab === 'queue' && (
        <QueueTab
          loading={loadingChecks}
          view={view}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          todayOnly={todayOnly}
          setTodayOnly={setTodayOnly}
          search={queueSearch}
          setSearch={setQueueSearch}
          locations={locations}
          activeLocationId={activeLocationId}
          setActiveLocationId={setActiveLocationId}
          hasMoreCompleted={hasMoreCompleted}
          onShowMoreCompleted={() => setCompletedLimit((c) => c + COMPLETED_PAGE_SIZE)}
          onCardTap={setDrawerCheck}
          onCheckedTap={setCheckedSheet}
          onReportIssueTap={setIssueSheet}
        />
      )}
      {tab === 'handovers' && (
        <HandoversTab loading={loadingHandovers} handovers={handovers} />
      )}
      {tab === 'bikes' && (
        <BikesTab
          loading={loadingBikes}
          bikes={bikes}
          search={bikeSearch}
          setSearch={setBikeSearch}
          onBikeTap={setBikeDetailFor}
        />
      )}

      {bikeDetailFor && (
        <BikeDetailSheet
          bike={bikeDetailFor}
          onClose={() => setBikeDetailFor(null)}
          onBlock={() => {
            const synth: EquipmentCheckListItem = makeSyntheticCheck(bikeDetailFor, activeLocationId || '');
            setBlockSheet(synth);
            setBikeDetailFor(null);
          }}
          onReportIssue={() => {
            const synth: EquipmentCheckListItem = makeSyntheticCheck(bikeDetailFor, activeLocationId || '');
            setIssueSheet(synth);
            setBikeDetailFor(null);
          }}
          onNavigateToIssue={handleNavigateToIssue}
          onResolveIssue={handleResolveIssue}
          onOpenPhoto={setPhotoViewerUrl}
        />
      )}

      {drawerCheck && (
        <DetailDrawer
          check={drawerCheck}
          activeLocation={activeLocation}
          onClose={() => setDrawerCheck(null)}
          onMarkChecked={() => { setCheckedSheet(drawerCheck); setDrawerCheck(null); }}
          onReportIssue={() => { setIssueSheet(drawerCheck); setDrawerCheck(null); }}
          onBlock={() => { setBlockSheet(drawerCheck); setDrawerCheck(null); }}
          onSwap={() => { setSwapSheet(drawerCheck); setDrawerCheck(null); }}
          onViewReturnReport={() => { setReturnReportFor(drawerCheck); setDrawerCheck(null); }}
          onNavigateToIssue={handleNavigateToIssue}
          onResolveIssue={handleResolveIssue}
          onOpenPhoto={setPhotoViewerUrl}
        />
      )}

      {photoViewerUrl && (
        <PhotoLightbox url={photoViewerUrl} onClose={() => setPhotoViewerUrl(null)} />
      )}

      {swapSheet && (
        <SwapPickerSheet
          check={swapSheet}
          onClose={() => setSwapSheet(null)}
          onConfirm={async (candidateId) => {
            await handleSwap(swapSheet, candidateId);
            setSwapSheet(null);
          }}
        />
      )}

      {returnReportFor && (
        <ReturnReportSheet
          bookingId={returnReportFor.booking_id}
          locationId={returnReportFor.location_id}
          onClose={() => setReturnReportFor(null)}
        />
      )}

      {checkedSheet && (
        <MarkCheckedSheet
          check={checkedSheet}
          onClose={() => setCheckedSheet(null)}
          onConfirm={(note, photos) => handleMarkChecked(checkedSheet.id, note, photos)}
        />
      )}

      {issueSheet && merchantId && (
        <ReportIssueModal
          isOpen={true}
          onClose={() => setIssueSheet(null)}
          onIssueCreated={(issue) => handleIssueCreated(issue, issueSheet.id || null)}
          initialData={{
            merchant_id: merchantId,
            location_id: issueSheet.location_id,
            inventory_item_id: issueSheet.inventory_item_id,
            booking_id: issueSheet.booking_id || null,
            source: 'internal',
            title: `${issueSheet.item_number} — ${t('equipmentCheck.issueSheetTitleSuffix')}`,
            item_label: `${issueSheet.item_number} · ${issueSheet.product_name || ''}`,
          }}
        />
      )}

      {blockSheet && (
        <BlockSheet
          check={blockSheet}
          onClose={() => setBlockSheet(null)}
          onConfirm={(until, reason) => handleBlock(blockSheet, until, reason)}
        />
      )}
    </div>
  );
}

// ---------- Queue Tab -------------------------------------------------------

interface QueueTabProps {
  loading: boolean;
  view: QueueView;
  statusFilter: StatusFilter;
  setStatusFilter: (s: StatusFilter) => void;
  todayOnly: boolean;
  setTodayOnly: (v: boolean) => void;
  search: string;
  setSearch: (s: string) => void;
  locations: LocationOption[];
  activeLocationId: string | null;
  setActiveLocationId: (id: string) => void;
  hasMoreCompleted: boolean;
  onShowMoreCompleted: () => void;
  onCardTap: (c: EquipmentCheckListItem) => void;
  onCheckedTap: (c: EquipmentCheckListItem) => void;
  onReportIssueTap: (c: EquipmentCheckListItem) => void;
}

function QueueTab({
  loading, view, statusFilter, setStatusFilter, todayOnly, setTodayOnly,
  search, setSearch,
  locations, activeLocationId, setActiveLocationId,
  hasMoreCompleted, onShowMoreCompleted,
  onCardTap, onCheckedTap, onReportIssueTap,
}: QueueTabProps) {
  const { t } = useTranslation();

  // Aggregate row count across all view shapes for the empty-state check.
  const total =
    view.kind === 'flat'
      ? view.items.length
      : view.kind === 'priority'
        ? view.urgent.length + view.attention.length + view.later.length
        : view.urgent.length + view.attention.length + view.later.length + view.completed.length;

  const renderCard = (c: EquipmentCheckListItem) => (
    <CheckCard key={c.id} check={c} onTap={() => onCardTap(c)}
      onChecked={() => onCheckedTap(c)} onIssue={() => onReportIssueTap(c)} />
  );

  return (
    <>
      <div className="-mx-md sm:-mx-lg px-md sm:px-lg py-sm bg-secondary border-b border-primary mb-md space-y-sm">
        <div className="flex items-center gap-sm">
          <Search className="w-4 h-4 text-secondary flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('equipmentCheck.searchQueuePlaceholder')}
            className="flex-1 max-w-full bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm box-border"
          />
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <div className="inline-flex rounded-md bg-primary border border-primary p-1">
            {(['pending', 'checked', 'all'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-sm py-xs text-xs font-medium rounded ${
                  statusFilter === s ? 'bg-blue text-white' : 'text-secondary hover:text-primary'
                }`}
              >
                {t(`equipmentCheck.status${s.charAt(0).toUpperCase() + s.slice(1)}`)}
              </button>
            ))}
          </div>

          {/* Today's-pickups toggle is meaningless for already-checked rows. */}
          {statusFilter !== 'checked' && (
            <label className="inline-flex items-center gap-xs text-sm text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={todayOnly}
                onChange={(e) => setTodayOnly(e.target.checked)}
                className="rounded"
              />
              {t('equipmentCheck.todayPickupsOnly')}
            </label>
          )}

          {locations.length > 1 && (
            <select
              value={activeLocationId || ''}
              onChange={(e) => setActiveLocationId(e.target.value)}
              className="sm:hidden ml-auto bg-primary border border-primary rounded-md px-sm py-xs text-base"
            >
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-xl">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
        </div>
      )}

      {!loading && total === 0 && (
        <div className="card text-center py-xl">
          <CheckCircle2 className="w-12 h-12 text-success mx-auto mb-md" />
          <h3 className="text-lg font-medium text-primary mb-sm">{t('equipmentCheck.allCaughtUp')}</h3>
          <p className="text-sm text-secondary">{t('equipmentCheck.noEquipmentPending')}</p>
        </div>
      )}

      {/* Pending tab — priority-grouped */}
      {!loading && view.kind === 'priority' && (
        <>
          {view.urgent.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupUrgent')} tone="danger" count={view.urgent.length}>
              {view.urgent.map(renderCard)}
            </PriorityGroup>
          )}
          {view.attention.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupAttention')} tone="warning" count={view.attention.length}>
              {view.attention.map(renderCard)}
            </PriorityGroup>
          )}
          {view.later.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupLater')} tone="success" count={view.later.length}>
              {view.later.map(renderCard)}
            </PriorityGroup>
          )}
        </>
      )}

      {/* Checked tab — flat list newest-first */}
      {!loading && view.kind === 'flat' && (
        <>
          <div className="grid sm:grid-cols-2 gap-md">
            {view.items.map(renderCard)}
          </div>
          {hasMoreCompleted && (
            <div className="text-center mt-md">
              <button onClick={onShowMoreCompleted} className="btn btn-secondary">
                {t('equipmentCheck.showMore')}
              </button>
            </div>
          )}
        </>
      )}

      {/* All tab — priority-grouped pending + "Recently completed" */}
      {!loading && view.kind === 'split' && (
        <>
          {view.urgent.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupUrgent')} tone="danger" count={view.urgent.length}>
              {view.urgent.map(renderCard)}
            </PriorityGroup>
          )}
          {view.attention.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupAttention')} tone="warning" count={view.attention.length}>
              {view.attention.map(renderCard)}
            </PriorityGroup>
          )}
          {view.later.length > 0 && (
            <PriorityGroup label={t('equipmentCheck.groupLater')} tone="success" count={view.later.length}>
              {view.later.map(renderCard)}
            </PriorityGroup>
          )}
          {view.completed.length > 0 && (
            <div className="mb-lg">
              <div className="flex items-center gap-sm mb-sm px-xs">
                <span className="w-2 h-2 rounded-full bg-secondary" />
                <h2 className="text-sm font-semibold text-primary">{t('equipmentCheck.recentlyCompleted')}</h2>
                <span className="text-xs text-secondary">({view.completed.length})</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-md">
                {view.completed.map(renderCard)}
              </div>
              {hasMoreCompleted && (
                <div className="text-center mt-md">
                  <button onClick={onShowMoreCompleted} className="btn btn-secondary">
                    {t('equipmentCheck.showMore')}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}

function PriorityGroup({
  label, tone, count, children,
}: { label: string; tone: 'danger' | 'warning' | 'success'; count: number; children: React.ReactNode }) {
  const dot = tone === 'danger' ? 'bg-danger' : tone === 'warning' ? 'bg-warning' : 'bg-success';
  return (
    <div className="mb-lg">
      <div className="flex items-center gap-sm mb-sm px-xs">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <h2 className="text-sm font-semibold text-primary">{label}</h2>
        <span className="text-xs text-secondary">({count})</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-md">{children}</div>
    </div>
  );
}

function CheckCard({
  check, onTap, onChecked, onIssue,
}: { check: EquipmentCheckListItem; onTap: () => void; onChecked: () => void; onIssue: () => void }) {
  const { t, i18n } = useTranslation();
  const customer = customerBadgeLabel(check.customer_return, t);
  const customerTone = {
    ok: 'bg-success/10 text-success',
    warn: 'bg-warning/10 text-warning',
    danger: 'bg-danger/10 text-danger',
    neutral: 'bg-secondary text-secondary',
  }[customer.tone];
  const priority = computePriority(check);
  const overdue = check.booking_state !== 'returned'
    && !check.booking_returned_at
    && check.booking_end_date
    && new Date(check.booking_end_date) < new Date();

  return (
    <div className="card p-md cursor-pointer hover:shadow-md transition-shadow" onClick={onTap}>
      <div className="flex items-start gap-sm mb-sm">
        <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${priorityColor[priority]}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-sm flex-wrap">
            <span className="text-base font-semibold text-primary">{check.item_number}</span>
            {check.product_name && <span className="text-sm text-secondary truncate">{check.product_name}</span>}
          </div>
        </div>
        {check.combination_code && (
          <div className="flex items-center gap-xs text-sm bg-secondary px-sm py-xs rounded font-mono">
            <Lock className="w-3 h-3 text-secondary" />
            <span className="text-primary">{check.combination_code}</span>
          </div>
        )}
      </div>

      <div className="text-sm text-secondary mb-xs flex items-center gap-xs flex-wrap">
        <User className="w-3.5 h-3.5" />
        <span className="text-primary">{check.customer_name || '—'}</span>
        {check.order_id && <><span>·</span><span>#{check.order_id}</span></>}
      </div>

      <div className="text-xs text-secondary mb-sm flex items-center gap-md flex-wrap">
        {check.status === 'checked' && check.checked_at ? (
          <>
            <span className="inline-flex items-center gap-xs">
              <CheckCircle2 className="w-3 h-3 text-success" />
              <span className="text-primary">
                {t('equipmentCheck.checkedAgo', { relative: timeAgo(check.checked_at, t) })}
                {check.checked_by_name && ` · ${t('equipmentCheck.by')} ${check.checked_by_name}`}
              </span>
            </span>
            {check.booking_returned_at && (
              <span className="inline-flex items-center gap-xs opacity-70">
                <Clock className="w-3 h-3" />
                {t('equipmentCheck.returnedAgo', { relative: timeAgo(check.booking_returned_at, t) })}
              </span>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-xs">
            <Clock className="w-3 h-3" />
            {check.booking_returned_at
              ? t('equipmentCheck.returnedAgo', { relative: timeAgo(check.booking_returned_at, t) })
              : check.booking_state === 'returned'
                ? t('equipmentCheck.returned')
                : overdue
                  ? <span className="text-warning font-medium">{t('equipmentCheck.overdueAgo', { relative: timeAgo(check.booking_end_date, t) })}</span>
                  : '—'}
          </span>
        )}
        {check.next_booking_start ? (
          <span className="inline-flex items-center gap-xs">
            <Calendar className="w-3 h-3" />
            {t('equipmentCheck.nextOut')}: <span className="text-primary font-medium">{formatNextOut(check.next_booking_start, t, i18n.language)}</span>
          </span>
        ) : (
          <span className="italic">{t('equipmentCheck.noUpcomingBooking')}</span>
        )}
      </div>

      <div className="flex items-center gap-xs flex-wrap mb-md">
        <span className={`inline-flex items-center gap-xs px-sm py-xs rounded-full text-xs font-medium ${customerTone}`}>
          {customer.label}
        </span>
        {check.customer_return_photos > 0 && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-secondary text-xs text-secondary">
            <ImageIcon className="w-3 h-3" />
            {check.customer_return_photos}
          </span>
        )}
        {check.open_issues.length > 0 && (
          <span
            className={`inline-flex items-center gap-xs px-sm py-xs rounded-full text-xs font-medium ${
              check.open_issues.some((i) => i.severity === 'critical' || i.severity === 'high')
                ? 'bg-danger/10 text-danger'
                : 'bg-warning/10 text-warning'
            }`}
            title={check.open_issues.map((i) => `${i.title} (${i.severity})`).join('\n')}
          >
            <Wrench className="w-3 h-3" />
            {check.open_issues.length === 1
              ? `${t('equipmentCheck.issuePrefix')}: ${check.open_issues[0].title}`
              : t('equipmentCheck.openIssuesMany', { count: check.open_issues.length })}
          </span>
        )}
        {check.customer_flagged_after_check && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-warning/10 text-warning text-xs font-medium">
            <AlertTriangle className="w-3 h-3" />
            {t('equipmentCheck.customerFlaggedAfterCheck')}
          </span>
        )}
        {check.status === 'checked' && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-success/10 text-success text-xs font-medium">
            <CheckCircle2 className="w-3 h-3" />
            {t('equipmentCheck.checkedBadge')}
          </span>
        )}
        {check.status === 'obsolete' && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-secondary text-secondary text-xs font-medium">
            {t('equipmentCheck.obsoleteBadge')}
          </span>
        )}
      </div>

      {check.status === 'pending' && (
        <div className="flex gap-sm" onClick={(e) => e.stopPropagation()}>
          <button onClick={onChecked} className="btn btn-primary flex-1 min-h-[44px]">
            <CheckCircle2 className="w-4 h-4" />
            <span>{t('equipmentCheck.actionChecked')}</span>
          </button>
          <button onClick={onIssue} className="btn btn-secondary flex-1 min-h-[44px]">
            <AlertTriangle className="w-4 h-4" />
            <span>{t('equipmentCheck.actionReportIssue')}</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Detail drawer ---------------------------------------------------

function DetailDrawer({
  check, activeLocation, onClose, onMarkChecked, onReportIssue, onBlock, onSwap, onViewReturnReport,
  onNavigateToIssue, onResolveIssue, onOpenPhoto,
}: {
  check: EquipmentCheckListItem;
  activeLocation: LocationOption | undefined;
  onClose: () => void;
  onMarkChecked: () => void;
  onReportIssue: () => void;
  onBlock: () => void;
  onSwap: () => void;
  onViewReturnReport: () => void;
  onNavigateToIssue: (issueId: string) => void;
  onResolveIssue: (issueId: string) => Promise<boolean>;
  onOpenPhoto: (url: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const customer = customerBadgeLabel(check.customer_return, t);
  const canSwap = swapAllowedFor(check, activeLocation);
  const hasReturnReport = check.customer_return.kind !== 'none';

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={`${check.item_number} · ${check.product_name || ''}`}>
      <div className="space-y-md">
        <Section label={t('equipmentCheck.sectionBooking')}>
          <div className="text-sm">
            <div className="flex items-center gap-xs mb-xs">
              <User className="w-4 h-4 text-secondary" />
              <span className="text-primary font-medium">{check.customer_name || '—'}</span>
              {check.order_id && <span className="text-secondary">· #{check.order_id}</span>}
            </div>
            <div className="text-secondary">
              {check.booking_returned_at
                ? t('equipmentCheck.returnedAgo', { relative: timeAgo(check.booking_returned_at, t) })
                : check.booking_state === 'returned'
                  ? t('equipmentCheck.returned')
                  : check.booking_end_date && new Date(check.booking_end_date) < new Date()
                    ? t('equipmentCheck.overdueAgo', { relative: timeAgo(check.booking_end_date, t) })
                    : '—'}
            </div>
          </div>
        </Section>

        {check.combination_code && (
          <Section label={t('equipmentCheck.combinationCode')}>
            <div className="text-2xl font-mono font-semibold text-primary tracking-widest">
              {check.combination_code}
            </div>
          </Section>
        )}

        {check.status === 'checked' && (check.checked_by_name || check.checked_at) && (
          <Section label={t('equipmentCheck.sectionCheckedBy')}>
            <div className="text-sm text-primary">
              {check.checked_by_name || '—'}
              {check.checked_at && (
                <span className="text-secondary">
                  {' · '}
                  {new Date(check.checked_at).toLocaleString(i18n.language, {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              )}
            </div>
          </Section>
        )}

        {check.status === 'checked' && check.note && (
          <Section label={t('equipmentCheck.noteLabel')}>
            <div className="text-sm text-primary italic">"{check.note}"</div>
          </Section>
        )}

        {check.status === 'checked' && check.photo_paths.length > 0 && (
          <Section label={t('equipmentCheck.photosLabel')}>
            <PhotoThumbnails paths={check.photo_paths} onOpen={onOpenPhoto} />
          </Section>
        )}

        <Section label={t('equipmentCheck.sectionCustomerReturn')}>
          <button
            type="button"
            onClick={hasReturnReport ? onViewReturnReport : undefined}
            disabled={!hasReturnReport}
            className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${
              customer.tone === 'ok' ? 'bg-success/10 text-success' :
              customer.tone === 'danger' ? 'bg-danger/10 text-danger' :
              customer.tone === 'warn' ? 'bg-warning/10 text-warning' :
              'bg-secondary text-secondary'
            } ${hasReturnReport ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
          >
            {customer.label}
            {hasReturnReport && <ChevronRight className="w-3 h-3 ml-xs" />}
          </button>
        </Section>

        {check.next_booking_start && (
          <Section label={t('equipmentCheck.sectionNextBooking')}>
            <div className="text-sm">
              <div className="text-primary font-medium">{formatNextOut(check.next_booking_start, t, i18n.language)}</div>
              {check.next_booking_customer && (
                <div className="text-secondary">{check.next_booking_customer}</div>
              )}
            </div>
          </Section>
        )}

        {check.open_issues.length > 0 && (
          <Section label={t('equipmentCheck.openIssuesHeading', { count: check.open_issues.length })}>
            <div className="space-y-xs">
              {check.open_issues.map((i) => (
                <OpenIssueRow
                  key={i.id}
                  issue={i}
                  onNavigate={onNavigateToIssue}
                  onResolve={onResolveIssue}
                />
              ))}
            </div>
          </Section>
        )}

        <div className="pt-sm border-t border-primary space-y-sm">
          <button
            onClick={onBlock}
            className="w-full flex items-center justify-between px-md py-sm rounded-md hover:bg-secondary text-left"
          >
            <span className="flex items-center gap-sm">
              <Lock className="w-4 h-4 text-warning" />
              <span className="text-sm font-medium text-primary">{t('equipmentCheck.blockFromRental')}</span>
            </span>
            <ChevronRight className="w-4 h-4 text-secondary" />
          </button>

          <div className="relative">
            <button
              onClick={onSwap}
              disabled={!canSwap}
              className="w-full flex items-center justify-between px-md py-sm rounded-md hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed text-left"
            >
              <span className="flex items-center gap-sm">
                <Package className="w-4 h-4 text-blue" />
                <span className="text-sm font-medium text-primary">{t('equipmentCheck.swapEquipmentUpcoming')}</span>
              </span>
              <ChevronRight className="w-4 h-4 text-secondary" />
            </button>
            {!canSwap && (
              <div className="text-xs text-secondary mt-xs ml-md flex items-center gap-xs">
                <Info className="w-3 h-3" />
                {!check.next_booking_start
                  ? t('equipmentCheck.swapHintNoBooking')
                  : !activeLocation?.two_emails_enabled
                    ? t('equipmentCheck.swapHintNoTwoEmail')
                    : t('equipmentCheck.swapHintPastCutoff')}
              </div>
            )}
          </div>
        </div>

        {check.status === 'pending' && (
          <div className="flex gap-sm pt-md border-t border-primary">
            <button onClick={onMarkChecked} className="btn btn-primary flex-1 min-h-[44px]">
              <CheckCircle2 className="w-4 h-4" />
              {t('equipmentCheck.actionChecked')}
            </button>
            <button onClick={onReportIssue} className="btn btn-secondary flex-1 min-h-[44px]">
              <AlertTriangle className="w-4 h-4" />
              {t('equipmentCheck.actionReportIssue')}
            </button>
          </div>
        )}
      </div>
    </BottomSheet>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-secondary mb-xs">{label}</div>
      {children}
    </div>
  );
}

// Renders a row of thumbnails for a list of equipment-check photo storage
// paths. The bucket is private so we batch-sign URLs on mount. Tapping a
// thumbnail invokes onOpen with the signed URL → page-level PhotoLightbox.
function PhotoThumbnails({
  paths, onOpen, small,
}: { paths: string[]; onOpen: (url: string) => void; small?: boolean }) {
  const [urls, setUrls] = useState<Array<string | null>>([]);
  useEffect(() => {
    let cancelled = false;
    if (paths.length === 0) { setUrls([]); return; }
    getEquipmentCheckPhotoSignedUrls(paths).then((rows) => {
      if (cancelled) return;
      setUrls(rows.map((r) => r.url));
    });
    return () => { cancelled = true; };
  // paths array identity changes between renders; serialise for stable dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths.join('|')]);
  const size = small ? 'w-12 h-12' : 'w-16 h-16';
  return (
    <div className="flex gap-xs flex-wrap">
      {paths.map((p, i) => {
        const url = urls[i];
        if (!url) {
          return <div key={p} className={`${size} rounded-md bg-secondary animate-pulse`} />;
        }
        return (
          <button
            key={p}
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(url); }}
            className={`${size} rounded-md overflow-hidden border border-primary bg-secondary`}
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
          </button>
        );
      })}
    </div>
  );
}

function PhotoLightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-md cursor-zoom-out"
    >
      <img
        src={url}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain"
      />
      <button
        onClick={onClose}
        className="absolute top-md right-md w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
        title="Close"
      >
        <X className="w-5 h-5" />
      </button>
    </div>,
    document.body,
  );
}

// Shared row for an open issue: whole row navigates to /issues/:id; a small
// "Resolve" button on the right is a one-tap quick action that marks the
// issue resolved with resolution_type='repaired'.
function OpenIssueRow({
  issue, onNavigate, onResolve,
}: {
  issue: { id: string; title: string; severity: string; status: string };
  onNavigate: (id: string) => void;
  onResolve: (id: string) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const [resolving, setResolving] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  const red = issue.severity === 'critical' || issue.severity === 'high';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onNavigate(issue.id)}
      onKeyDown={(e) => { if (e.key === 'Enter') onNavigate(issue.id); }}
      className="w-full flex items-center gap-xs text-left p-sm rounded-md hover:bg-secondary transition-colors cursor-pointer"
    >
      <Wrench className={`w-3.5 h-3.5 flex-shrink-0 ${red ? 'text-danger' : 'text-warning'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-primary font-medium truncate">{issue.title}</div>
        <div className="text-xs text-secondary">{issue.severity} · {issue.status}</div>
      </div>
      <button
        type="button"
        onClick={async (e) => {
          e.stopPropagation();
          setResolving(true);
          try {
            const ok = await onResolve(issue.id);
            if (ok) setHidden(true);
          } finally {
            setResolving(false);
          }
        }}
        disabled={resolving}
        className="text-xs px-sm py-xs rounded-full bg-success/10 text-success font-medium hover:bg-success/20 disabled:opacity-50"
      >
        {resolving ? <Loader2 className="w-3 h-3 animate-spin" /> : t('equipmentCheck.resolve')}
      </button>
      <ChevronRight className="w-4 h-4 text-secondary flex-shrink-0" />
    </div>
  );
}

// ---------- Mark-checked sheet ---------------------------------------------

function MarkCheckedSheet({
  check, onClose, onConfirm,
}: { check: EquipmentCheckListItem; onClose: () => void; onConfirm: (note: string, photos: File[]) => Promise<void> | void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    await onConfirm(note, photos);
    setSubmitting(false);
  };

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={t('equipmentCheck.markCheckedTitle', { item: check.item_number })}>
      <div className="space-y-md">
        <div>
          <label className="text-sm font-medium text-primary mb-xs block">
            {t('equipmentCheck.noteLabel')} <span className="text-secondary font-normal">({t('equipmentCheck.optional')})</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('equipmentCheck.noteExample')}
            rows={3}
            className="w-full max-w-full bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm resize-none box-border"
          />
        </div>

        <PhotoPicker photos={photos} setPhotos={setPhotos} max={3} />

        <div className="flex gap-sm pt-sm border-t border-primary">
          <button onClick={onClose} className="btn btn-secondary flex-1 min-h-[44px]" disabled={submitting}>{t('equipmentCheck.cancel')}</button>
          <button onClick={submit} className="btn btn-primary flex-1 min-h-[44px]" disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {t('equipmentCheck.confirm')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------- Block sheet ----------------------------------------------------

function BlockSheet({
  check, onClose, onConfirm,
}: { check: EquipmentCheckListItem; onClose: () => void; onConfirm: (until: string | null, reason: string) => Promise<void> | void }) {
  const { t } = useTranslation();
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [indefinite, setIndefinite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    await onConfirm(indefinite ? null : until, reason);
    setSubmitting(false);
  };

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={t('equipmentCheck.blockTitle', { item: check.item_number })}>
      <div className="space-y-md">
        <div>
          <label className="text-sm font-medium text-primary mb-xs block">{t('equipmentCheck.blockUntilLabel')}</label>
          <div className="space-y-xs">
            <label className="flex items-center gap-sm text-sm cursor-pointer">
              <input type="radio" checked={indefinite} onChange={() => setIndefinite(true)} />
              {t('equipmentCheck.untilFurtherNotice')}
            </label>
            <label className="flex items-center gap-sm text-sm cursor-pointer">
              <input type="radio" checked={!indefinite} onChange={() => setIndefinite(false)} />
              {t('equipmentCheck.specificDate')}:
              <input
                type="date"
                disabled={indefinite}
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm disabled:opacity-50"
              />
            </label>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-primary mb-xs block">{t('equipmentCheck.reasonLabel')}</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('equipmentCheck.reasonExample')}
            className="w-full max-w-full bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm box-border"
          />
        </div>

        <div className="flex gap-sm pt-sm border-t border-primary">
          <button onClick={onClose} className="btn btn-secondary flex-1 min-h-[44px]" disabled={submitting}>{t('equipmentCheck.cancel')}</button>
          <button onClick={submit} className="btn btn-primary flex-1 min-h-[44px]" disabled={submitting || !reason.trim()}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {t('equipmentCheck.blockAction')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------- Photo picker ----------------------------------------------------

function PhotoPicker({
  photos, setPhotos, max,
}: { photos: File[]; setPhotos: (p: File[]) => void; max: number }) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const remaining = max - photos.length;
  return (
    <div>
      <label className="text-sm font-medium text-primary mb-xs block">
        {t('equipmentCheck.photosLabel')} <span className="text-secondary font-normal">({t('equipmentCheck.photosOptionalUpTo', { n: max })})</span>
      </label>
      <div className="flex gap-sm flex-wrap">
        {photos.map((file, i) => (
          <div key={i} className="relative w-20 h-20 rounded-md bg-secondary border border-primary overflow-hidden">
            <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
              className="absolute top-0 right-0 p-0.5 bg-danger text-white rounded-bl"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {remaining > 0 && (
          <>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="w-20 h-20 rounded-md border-2 border-dashed border-primary flex flex-col items-center justify-center text-secondary hover:border-blue hover:text-blue transition-colors"
            >
              <Camera className="w-5 h-5 mb-xs" />
              <span className="text-xs">{t('equipmentCheck.addPhoto')}</span>
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              hidden
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, remaining);
                setPhotos([...photos, ...files]);
                if (e.target) e.target.value = '';
              }}
            />
          </>
        )}
      </div>
      <p className="text-xs text-secondary mt-xs">{t('equipmentCheck.photosCompressedHint')}</p>
    </div>
  );
}

// ---------- Handovers Tab ---------------------------------------------------

function HandoversTab({ loading, handovers }: { loading: boolean; handovers: UpcomingHandover[] }) {
  const { t, i18n } = useTranslation();
  const groups = useMemo(() => {
    const byDay = new Map<string, UpcomingHandover[]>();
    for (const h of handovers) {
      const d = new Date(h.start_date);
      const key = d.toDateString();
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(h);
    }
    return Array.from(byDay.entries()).sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());
  }, [handovers]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-xl">
        <Loader2 className="w-6 h-6 animate-spin text-secondary" />
      </div>
    );
  }

  if (handovers.length === 0) {
    return (
      <div className="card text-center py-xl">
        <Calendar className="w-12 h-12 text-secondary mx-auto mb-md" />
        <h3 className="text-lg font-medium text-primary mb-sm">{t('equipmentCheck.noUpcomingHandovers')}</h3>
        <p className="text-sm text-secondary">{t('equipmentCheck.noUpcomingHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-lg">
      <p className="text-sm text-secondary">{t('equipmentCheck.next7Days')}</p>
      {groups.map(([day, list]) => (
        <div key={day}>
          <h2 className="text-sm font-semibold text-primary mb-sm">
            {new Date(day).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'short' })}
          </h2>
          <div className="space-y-sm">
            {list.map((h) => (
              <div key={h.booking_id} className="card p-md">
                <div className="flex items-baseline justify-between mb-sm">
                  <div>
                    <div className="text-primary font-medium">{h.customer_name || '—'}</div>
                    <div className="text-xs text-secondary">#{h.order_id}</div>
                  </div>
                  <div className="text-sm font-medium text-primary">
                    {new Date(h.start_date).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>

                {h.tracked.length > 0 && (
                  <div className="mb-sm">
                    <div className="text-xs font-semibold uppercase tracking-wider text-secondary mb-xs">
                      {t('equipmentCheck.sectionEquipment')}
                    </div>
                    <div className="space-y-xs">
                      {h.tracked.map((it) => {
                        const ready = it.last_check_status === 'checked';
                        const out = it.last_check_status === 'currently_out';
                        const statusText = out
                          ? t('equipmentCheck.outUntil', {
                              when: it.currently_out_end_date
                                ? formatNextOut(it.currently_out_end_date, t, i18n.language)
                                : '—',
                            })
                          : ready
                            ? t('equipmentCheck.checkedReady')
                            : t('equipmentCheck.checkPending');
                        return (
                          <div key={it.inventory_item_id} className="flex items-center gap-sm text-sm flex-wrap">
                            {ready ? (
                              <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" />
                            ) : (
                              <Clock className="w-4 h-4 text-warning flex-shrink-0" />
                            )}
                            {it.attention && (
                              <span
                                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                  it.attention === 'red' ? 'bg-danger' : 'bg-warning'
                                }`}
                                title={it.attention_reason || ''}
                              />
                            )}
                            <span className="text-primary font-medium">{it.item_number}</span>
                            {it.product_name && <span className="text-secondary">{it.product_name}</span>}
                            {it.combination_code && !out && (
                              <span className="font-mono text-xs bg-secondary px-xs rounded text-primary">
                                {it.combination_code}
                              </span>
                            )}
                            <span className={`ml-auto text-xs ${ready ? 'text-success' : 'text-warning'}`}>
                              {statusText}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {h.addons.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-secondary mb-xs">
                      {t('equipmentCheck.sectionAddons')}
                    </div>
                    <div className="space-y-xs">
                      {h.addons.map((a) => (
                        <div key={a.booking_item_id} className="text-sm text-secondary">
                          <span className="text-primary font-medium">{a.quantity} ×</span>{' '}
                          <span>{a.item_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Swap picker sheet ----------------------------------------------

function SwapPickerSheet({
  check, onClose, onConfirm,
}: {
  check: EquipmentCheckListItem;
  onClose: () => void;
  onConfirm: (candidateId: string) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<SwapCandidate[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      if (!check.product_type_id || !check.next_booking_id || !check.next_booking_start || !check.next_booking_end) {
        if (!cancelled) {
          setError(t('equipmentCheck.swapMissingContext'));
          setLoading(false);
        }
        return;
      }
      const { data, error: err } = await getSwapCandidates({
        currentInventoryItemId: check.inventory_item_id,
        productTypeId: check.product_type_id,
        locationId: check.location_id,
        bookingId: check.next_booking_id,
        windowStart: check.next_booking_start,
        windowEnd: check.next_booking_end,
      });
      if (cancelled) return;
      if (err) setError(err);
      setCandidates(data);
      setLoading(false);
    }
    run();
    return () => { cancelled = true; };
  }, [check]);

  const submit = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    await onConfirm(selectedId);
    setSubmitting(false);
  };

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={t('equipmentCheck.swapTitle', { item: check.item_number })}>
      <div className="space-y-md">
        <div className="text-xs text-secondary p-sm bg-secondary rounded-md flex items-start gap-xs">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            {t('equipmentCheck.swapPickerHint', {
              when: check.next_booking_start ? formatNextOut(check.next_booking_start, t, i18n.language) : '—',
            })}
          </span>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-lg">
            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
          </div>
        )}

        {error && <div className="text-sm text-danger">{error}</div>}

        {!loading && !error && candidates.length === 0 && (
          <div className="text-center text-sm text-secondary py-md">
            {t('equipmentCheck.swapNoCandidates')}
          </div>
        )}

        {!loading && candidates.length > 0 && (
          <div className="space-y-xs max-h-[50vh] overflow-y-auto">
            {candidates.map((c) => {
              const disabled = c.status !== 'available';
              const selected = selectedId === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => !disabled && setSelectedId(c.id)}
                  disabled={disabled}
                  className={`w-full flex items-center gap-sm px-md py-sm rounded-md border text-left transition-colors ${
                    selected ? 'border-blue bg-blue/5' :
                    disabled ? 'border-primary opacity-60 cursor-not-allowed' :
                    'border-primary hover:bg-secondary'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-sm">
                      <span className="text-sm font-semibold text-primary">{c.item_number}</span>
                      {c.size && <span className="text-xs text-secondary">{c.size}</span>}
                      {c.combination_code && (
                        <span className="font-mono text-xs bg-secondary px-xs rounded">{c.combination_code}</span>
                      )}
                    </div>
                    {c.conflict_summary && (
                      <div className="text-xs text-warning mt-xs">{c.conflict_summary}</div>
                    )}
                  </div>
                  <span className={`text-xs px-sm py-xs rounded-full font-medium ${
                    c.status === 'available' ? 'bg-success/10 text-success' :
                    c.status === 'blocked'   ? 'bg-warning/10 text-warning' :
                    c.status === 'conflict'  ? 'bg-danger/10 text-danger' :
                                               'bg-secondary text-secondary'
                  }`}>
                    {t(`equipmentCheck.swapStatus${c.status.charAt(0).toUpperCase() + c.status.slice(1)}`)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-sm pt-sm border-t border-primary">
          <button onClick={onClose} className="btn btn-secondary flex-1 min-h-[44px]" disabled={submitting}>{t('equipmentCheck.cancel')}</button>
          <button
            onClick={submit}
            disabled={!selectedId || submitting}
            className="btn btn-primary flex-1 min-h-[44px] disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
            {t('equipmentCheck.swap')}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// ---------- Return report sheet --------------------------------------------

function ReturnReportSheet({
  bookingId, locationId, onClose,
}: { bookingId: string; locationId: string; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <BottomSheet isOpen={true} onClose={onClose} title={t('equipmentCheck.returnReportTitle')}>
      <ReturnReportDetail bookingId={bookingId} locationId={locationId} />
    </BottomSheet>
  );
}

// ---------- Bikes tab -------------------------------------------------------

function BikesTab({
  loading, bikes, search, setSearch, onBikeTap,
}: {
  loading: boolean;
  bikes: BikeListItem[];
  search: string;
  setSearch: (s: string) => void;
  onBikeTap: (b: BikeListItem) => void;
}) {
  const { t } = useTranslation();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bikes;
    return bikes.filter((b) =>
      b.item_number.toLowerCase().includes(q) ||
      (b.product_name || '').toLowerCase().includes(q) ||
      (b.combination_code || '').toLowerCase().includes(q) ||
      (b.size || '').toLowerCase().includes(q)
    );
  }, [bikes, search]);

  return (
    <>
      <div className="-mx-md sm:-mx-lg px-md sm:px-lg py-sm bg-secondary border-b border-primary mb-md">
        <div className="flex items-center gap-sm">
          <Search className="w-4 h-4 text-secondary flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('equipmentCheck.searchBikesPlaceholder')}
            className="flex-1 max-w-full bg-primary border border-primary rounded-md px-sm py-xs text-base sm:text-sm box-border"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-xl">
          <Loader2 className="w-6 h-6 animate-spin text-secondary" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="card text-center py-xl">
          <Package className="w-12 h-12 text-secondary mx-auto mb-md" />
          <h3 className="text-lg font-medium text-primary mb-sm">{t('equipmentCheck.bikesEmpty')}</h3>
          <p className="text-sm text-secondary">
            {search ? t('equipmentCheck.bikesNoMatch') : t('equipmentCheck.bikesNone')}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-md">
          {filtered.map((b) => <BikeRow key={b.inventory_item_id} bike={b} onTap={() => onBikeTap(b)} />)}
        </div>
      )}
    </>
  );
}

function BikeRow({ bike, onTap }: { bike: BikeListItem; onTap: () => void }) {
  const { t } = useTranslation();
  const blocked = bike.currently_blocked_until !== null;
  return (
    <div
      onClick={onTap}
      className="card p-md cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="flex items-start gap-sm mb-xs">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-sm flex-wrap">
            <span className="text-base font-semibold text-primary">{bike.item_number}</span>
            {bike.product_name && <span className="text-sm text-secondary truncate">{bike.product_name}</span>}
            {bike.size && <span className="text-xs text-secondary">{bike.size}</span>}
          </div>
        </div>
        {bike.combination_code && (
          <div className="flex items-center gap-xs text-sm bg-secondary px-sm py-xs rounded font-mono">
            <Lock className="w-3 h-3 text-secondary" />
            <span className="text-primary">{bike.combination_code}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-xs flex-wrap">
        {blocked && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-warning/10 text-warning text-xs font-medium">
            <Lock className="w-3 h-3" />
            {t('equipmentCheck.blocked')}
          </span>
        )}
        {bike.open_issues_count > 0 && (
          <span className={`inline-flex items-center gap-xs px-sm py-xs rounded-full text-xs font-medium ${
            bike.open_issues_max_severity === 'critical' || bike.open_issues_max_severity === 'high'
              ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'
          }`}>
            <Wrench className="w-3 h-3" />
            {bike.open_issues_count === 1
              ? t('equipmentCheck.openIssueOne')
              : t('equipmentCheck.openIssuesMany', { count: bike.open_issues_count })}
          </span>
        )}
        {bike.has_pending_check && (
          <span className="inline-flex items-center gap-xs px-sm py-xs rounded-full bg-blue/10 text-blue text-xs font-medium">
            <ClipboardCheck className="w-3 h-3" />
            {t('equipmentCheck.pendingCheck')}
          </span>
        )}
      </div>
    </div>
  );
}

function BikeDetailSheet({
  bike, onClose, onBlock, onReportIssue, onNavigateToIssue, onResolveIssue, onOpenPhoto,
}: {
  bike: BikeListItem;
  onClose: () => void;
  onBlock: () => void;
  onReportIssue: () => void;
  onNavigateToIssue: (issueId: string) => void;
  onResolveIssue: (issueId: string) => Promise<boolean>;
  onOpenPhoto: (url: string) => void;
}) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<BikeDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      const { data } = await getBikeDetail({ inventoryItemId: bike.inventory_item_id });
      if (!cancelled) {
        setDetail(data);
        setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [bike.inventory_item_id]);

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={`${bike.item_number} · ${bike.product_name || ''}`}>
      <div className="space-y-md">
        {bike.combination_code && (
          <Section label={t('equipmentCheck.combinationCode')}>
            <div className="text-2xl font-mono font-semibold text-primary tracking-widest">
              {bike.combination_code}
            </div>
          </Section>
        )}

        <Section label={t('equipmentCheck.itemInfo')}>
          <div className="text-sm space-y-xs">
            {bike.size && <div className="text-secondary">Size: <span className="text-primary">{bike.size}</span></div>}
            <div className="text-secondary">
              {t('equipmentCheck.lastMaintenance')}: <span className="text-primary">
                {bike.last_maintenance_date
                  ? new Date(bike.last_maintenance_date).toLocaleDateString()
                  : '—'}
              </span>
            </div>
          </div>
        </Section>

        {bike.currently_blocked_until && (
          <Section label={t('equipmentCheck.blockedFromRental')}>
            <div className="text-sm">
              <div className="text-warning font-medium">
                {t('equipmentCheck.blockedUntil')}: {new Date(bike.currently_blocked_until).toLocaleDateString()}
              </div>
              {bike.blocking_reason && (
                <div className="text-xs text-secondary mt-xs">{bike.blocking_reason}</div>
              )}
            </div>
          </Section>
        )}

        {loading && (
          <div className="flex items-center justify-center py-md">
            <Loader2 className="w-5 h-5 animate-spin text-secondary" />
          </div>
        )}

        {detail && detail.open_issues.length > 0 && (
          <Section label={t('equipmentCheck.openIssuesHeading', { count: detail.open_issues.length })}>
            <div className="space-y-xs">
              {detail.open_issues.map((i) => (
                <OpenIssueRow
                  key={i.id}
                  issue={i}
                  onNavigate={onNavigateToIssue}
                  onResolve={onResolveIssue}
                />
              ))}
            </div>
          </Section>
        )}

        {detail && detail.recent_checks.length > 0 && (
          <Section label={t('equipmentCheck.recentChecks')}>
            <div className="space-y-xs">
              {detail.recent_checks.map((c) => (
                <div key={c.id} className="text-sm border-b border-primary pb-xs last:border-0">
                  <div className="flex items-center gap-xs">
                    <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${
                      c.status === 'checked' ? 'bg-success/10 text-success' :
                      c.status === 'pending' ? 'bg-warning/10 text-warning' :
                      'bg-secondary text-secondary'
                    }`}>
                      {c.status}
                    </span>
                    <span className="text-xs text-secondary">
                      {c.checked_at
                        ? `${t('equipmentCheck.checkedOn')} ${new Date(c.checked_at).toLocaleDateString()}`
                        : new Date(c.created_at).toLocaleDateString()}
                      {c.checked_by_name && (
                        <> · {t('equipmentCheck.by')} {c.checked_by_name}</>
                      )}
                    </span>
                  </div>
                  <div className="text-xs text-secondary mt-xs">
                    {c.customer_name || '—'} {c.order_id && `· #${c.order_id}`}
                  </div>
                  {c.note && <div className="text-xs text-primary mt-xs italic">"{c.note}"</div>}
                  {c.photo_paths.length > 0 && (
                    <div className="mt-xs">
                      <PhotoThumbnails paths={c.photo_paths} onOpen={onOpenPhoto} small />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        <div className="pt-sm border-t border-primary space-y-sm">
          <button
            onClick={onBlock}
            className="w-full flex items-center justify-between px-md py-sm rounded-md hover:bg-secondary text-left"
          >
            <span className="flex items-center gap-sm">
              <Lock className="w-4 h-4 text-warning" />
              <span className="text-sm font-medium text-primary">{t('equipmentCheck.blockFromRental')}</span>
            </span>
            <ChevronRight className="w-4 h-4 text-secondary" />
          </button>
          <button
            onClick={onReportIssue}
            className="w-full flex items-center justify-between px-md py-sm rounded-md hover:bg-secondary text-left"
          >
            <span className="flex items-center gap-sm">
              <AlertTriangle className="w-4 h-4 text-danger" />
              <span className="text-sm font-medium text-primary">{t('equipmentCheck.reportIssue')}</span>
            </span>
            <ChevronRight className="w-4 h-4 text-secondary" />
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}
